import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, CheckCircle, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AwaitingApproval() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect if user is not authenticated or doesn't need approval
  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (user.isApproved) {
      // User is approved, redirect to their dashboard
      switch (user.role) {
        case 'admin':
          navigate('/admin/dashboard');
          break;
        case 'mentor':
          navigate('/mentor/dashboard');
          break;
        case 'aspirant':
          navigate('/aspirant/dashboard');
          break;
        default:
          navigate('/');
      }
    } else if (!user.isEmailVerified) {
      // User still needs email verification
      navigate('/verify-email');
    }
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100 p-4">
      <Card className="w-full max-w-md bg-surface shadow-xl border-0">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 p-3 bg-amber-100 rounded-full w-fit">
            <Clock className="h-8 w-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-secondary">
            Awaiting Admin Approval
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            Your account is being reviewed by our admin team
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-semibold text-green-800">Email Verified</p>
                <p className="text-sm text-primary">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-semibold text-amber-800">Pending Admin Review</p>
                <p className="text-sm text-amber-600">Your {user.role} application is under review</p>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="bg-background p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-secondary mb-3">Application Status</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-primary rounded-full flex-shrink-0"></div>
                <div className="text-sm">
                  <span className="font-medium text-secondary">Registration Complete</span>
                  <p className="text-gray-600">Account created successfully</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-primary rounded-full flex-shrink-0"></div>
                <div className="text-sm">
                  <span className="font-medium text-secondary">Email Verified</span>
                  <p className="text-gray-600">Email address confirmed</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-amber-500 rounded-full flex-shrink-0 animate-pulse"></div>
                <div className="text-sm">
                  <span className="font-medium text-secondary">Admin Review</span>
                  <p className="text-gray-600">Currently under review</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-gray-300 rounded-full flex-shrink-0"></div>
                <div className="text-sm">
                  <span className="font-medium text-gray-500">Account Activation</span>
                  <p className="text-gray-500">Pending approval</p>
                </div>
              </div>
            </div>
          </div>

          {/* Information */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-800">What's Next?</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Our admin team typically reviews applications within 24-48 hours. 
                  You'll receive an email notification once your account is approved and you can access all platform features.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center pt-4 border-t border-border">
            <p className="text-xs text-gray-500">
              Having trouble? Contact support for assistance
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 